import React, { createContext, useContext, ReactNode } from 'react';
import useFeatureFlag from './useFeatureFlag';
import { SUPPORTED_LANGUAGES, Language } from 'src/utils/i18n-config';
import { interpolateVariables } from 'src/hooks/interpolateVariables';

// Re-export for backward compatibility
export const languages = SUPPORTED_LANGUAGES;
export type { Language };

// I18n Context for provider pattern
interface I18nContextType {
  language: Language;
  defaultLanguage: Language;
  translations: Record<string, string>;
  t: (key: string, vars?: Record<string, string | number>) => string;
  changeLanguage: (lang: Language) => void;
}

const I18nContext = createContext<I18nContextType | null>(null);

// Hook to use I18n context
export const useI18nContext = (): I18nContextType => {
  const context = useContext(I18nContext);
  if (!context) {
    // Fallback when no context is provided
    return {
      language: 'en' as Language,
      defaultLanguage: 'en' as Language,
      translations: {},
      t: (key: string, vars?: Record<string, string | number>) => interpolateVariables(key, vars),
      changeLanguage: () => {
        // No-op when no context is provided
      },
    };
  }
  return context;
};

// Provider component that reads from Gatsby's page context
interface I18nProviderProps {
  children: ReactNode;
  pageContext?: {
    language?: Language;
    translations?: Record<string, string>;
  };
}

export const I18nProvider: React.FC<I18nProviderProps> = ({ children, pageContext }) => {
  // Get language and translations from props (passed from gatsby-browser/ssr)
  const language = pageContext?.language || ('en' as Language);
  const translations = pageContext?.translations || {};

  // Check if debug mode is enabled
  const i18nDebug = typeof window !== 'undefined' && useFeatureFlag('i18n_debug');

  // Debug logging
  if (i18nDebug && language !== 'en') {
    console.log('🌍 I18n Debug:', {
      language,
      translationCount: Object.keys(translations).length,
      hasTestTranslations: translations['Online Courses'] ? true : false,
    });
  }

  const t = (key: string, vars?: Record<string, string | number>): string => {
    // Trim the key to handle leading/trailing whitespace
    const trimmedKey = key.trim();

    // Return translation if it exists
    const translation = translations[trimmedKey];
    if (translation) {
      // If not in debug mode and it starts with [TRANSLATE], remove the prefix
      if (!i18nDebug && translation.startsWith('[TRANSLATE] ')) {
        return interpolateVariables(translation.replace('[TRANSLATE] ', ''), vars);
      }
      return interpolateVariables(translation, vars);
    }

    // Fallback to the key itself (English content)
    return interpolateVariables(key, vars);
  };

  const changeLanguage = (newLanguage: Language) => {
    // This would need to navigate to the new language URL
    if (i18nDebug) {
      console.log(`Language change requested: ${newLanguage}`);
    }
  };

  const contextValue: I18nContextType = {
    language,
    defaultLanguage: 'en' as Language,
    translations,
    t,
    changeLanguage,
  };

  return <I18nContext.Provider value={contextValue}>{children}</I18nContext.Provider>;
};
